// Archivo: inventory/SalidasInventario.js

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, doc, query, orderBy, runTransaction, getDocs, where, serverTimestamp, writeBatch } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Modal from '../../ui/Modal';
import ExitForm from '../../ui/ExitForm';
import ExitBatchDetailView from '../../ui/ExitBatchDetailView';

const PrintIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2-4h6m-6-4v2m6-4v2" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 hover:text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
// --- CAMBIO: Se añade el ícono de editar ---
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;


export default function SalidasInventario({ currentUser, db, showModal, catalogs, stock, permissions }) {
    const [exits, setExits] = useState([]);
    const [batchItems, setBatchItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedBatchForDetail, setSelectedBatchForDetail] = useState(null);
    // --- CAMBIO: Se añade estado para la edición ---
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        const q = query(collection(db, 'inventory_exits'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setExits(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db]);

    const groupedExits = useMemo(() => {
        if (!exits.length || !catalogs) return [];
        const groups = exits.reduce((acc, e) => {
            const batchId = e.batchId || e.id;
            if (!acc[batchId]) {
                acc[batchId] = {
                    batchId, status: e.status, requester: e.requester,
                    approver: e.approver,
                    createdAt: e.createdAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    approvedAt: e.approvedAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    items: [], totalQuantity: 0,
                };
            }
            const product = catalogs.products.find(p => p.id === e.productId);
            const design = catalogs.designs.find(d => d.id === e.designId);
            const location = catalogs.locations.find(l => l.id === e.locationId);
            
            // --- CAMBIO: Se mejora el nombre del diseño para incluir talla ---
            const designName = design ? `${design.name}${e.designSize ? ` (${e.designSize})` : ''}` : 'Liso';

            acc[batchId].items.push({
                ...e,
                productName: product?.name,
                designName: designName,
                locationName: location?.name,
            });
            acc[batchId].totalQuantity += e.quantity;
            return acc;
        }, {});
        return Object.values(groups);
    }, [exits, catalogs]);

    // --- CAMBIO: Se añade totalizador para el lote de salida ---
    const batchTotalQuantity = useMemo(() => {
        return batchItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    }, [batchItems]);

    // --- CAMBIO: Lógica para "congelar" el stock ---
    const availableStockForForm = useMemo(() => {
        if (!stock) return [];
        const subtractions = batchItems.reduce((acc, item) => {
            if (editingItem && item.tempId === editingItem.tempId) {
                return acc;
            }
            acc[item.stockItem.id] = (acc[item.stockItem.id] || 0) + item.quantity;
            return acc;
        }, {});
        return stock
            .map(item => {
                if (subtractions[item.id]) {
                    return { ...item, quantity: item.quantity - subtractions[item.id] };
                }
                return item;
            })
            .filter(item => item.quantity > 0);
    }, [stock, batchItems, editingItem]);


    const handleViewDetails = (batch) => {
        setSelectedBatchForDetail(batch);
        setIsDetailModalOpen(true);
    };

    const handleAddToBatch = (formData) => {
        setBatchItems(prev => [...prev, { ...formData, tempId: Date.now() }]);
        setIsModalOpen(false);
    };

    // --- CAMBIO: Se añade la función para actualizar un ítem del lote ---
    const handleUpdateBatchItem = (formData) => {
        const updatedItem = { ...formData, tempId: editingItem.tempId };
        setBatchItems(prevItems => prevItems.map(item => item.tempId === editingItem.tempId ? updatedItem : item));
        setEditingItem(null);
        setIsModalOpen(false);
    };
    
    // --- CAMBIO: Se añade la función para iniciar la edición ---
    const handleStartEdit = (itemToEdit) => {
        setEditingItem(itemToEdit);
        setIsModalOpen(true);
    };

    const handleRemoveFromBatch = (tempId) => {
        setBatchItems(prev => prev.filter(item => item.tempId !== tempId));
    };

    const handleSaveExitBatch = async () => {
        if (batchItems.length === 0) return showModal("Lote Vacío", "No hay salidas en el lote.");
        setIsLoading(true);
        const batchId = `salida-${Date.now()}`;
        const batch = writeBatch(db);
        batchItems.forEach(item => {
            const exitRef = doc(collection(db, 'inventory_exits'));
            batch.set(exitRef, {
                batchId,
                productId: item.stockItem.productId,
                designId: item.stockItem.designId,
                designSize: item.stockItem.designSize || null,
                size: item.stockItem.size,
                color: item.stockItem.color,
                locationId: item.stockItem.locationId,
                quantity: item.quantity,
                reason: item.reason,
                status: 'pendiente',
                requester: currentUser.name,
                requesterId: currentUser.uid,
                createdAt: serverTimestamp()
            });
        });
        try {
            await batch.commit();
            showModal("Éxito", "Lote de salidas solicitado. Esperando aprobación de un supervisor.");
            setBatchItems([]);
        } catch (error) {
            showModal("Error", `No se pudo crear el lote de salidas: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleExitAction = async (batchToProcess, newStatus) => {
        setIsLoading(true);
        const batch = writeBatch(db);
        const batchDocsQuery = query(collection(db, 'inventory_exits'), where("batchId", "==", batchToProcess.batchId));
        try {
            const batchDocsSnap = await getDocs(batchDocsQuery);
            if (batchDocsSnap.empty) throw new Error("No se encontraron los documentos del lote.");
            if (newStatus === 'aprobado') {
                await runTransaction(db, async (transaction) => {
                    for (const item of batchToProcess.items) {
                        const sku = `${item.productId}_${item.designId || 'NONE'}_${item.size}_${item.color}_${item.designSize || 'NONE'}_${item.locationId}`;
                        const stockQuery = query(collection(db, 'inventory_stock'), where("sku", "==", sku));
                        const stockSnap = await getDocs(stockQuery);
                        if (stockSnap.empty || stockSnap.docs[0].data().quantity < item.quantity) {
                            throw new Error(`Stock insuficiente para ${item.productName}. Operación cancelada.`);
                        }
                        const stockDoc = stockSnap.docs[0];
                        const newQty = stockDoc.data().quantity - item.quantity;
                        transaction.update(stockDoc.ref, { quantity: newQty });
                    }
                });
            }
            batchDocsSnap.forEach(docSnap => {
                batch.update(docSnap.ref, { status: newStatus, approver: currentUser.name, approvedAt: serverTimestamp() });
            });
            await batch.commit();
            showModal("Éxito", `Lote de salidas ${newStatus}.`);
        } catch (error) {
            showModal("Error", `No se pudo procesar el lote: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handlePrintBatch = (batch) => {
        const doc = new jsPDF();
        const logoImg = document.getElementById('company-logo-for-pdf');
        if (logoImg) doc.addImage(logoImg, 'PNG', 15, 12, 50, 15);
        doc.setFontSize(18);
        doc.text(`Comprobante de Salida de Lote`, 15, 40);
        doc.setFontSize(12);
        doc.text(`ID Lote: ${batch.batchId}`, 15, 47)
        doc.setFontSize(10);
        doc.setTextColor(100);
        const statusText = batch.status.charAt(0).toUpperCase() + batch.status.slice(1);
        const requesterLine = `Solicitado por: ${batch.requester || 'N/A'} el ${batch.createdAt || 'N/A'}`;
        const approverLine = batch.status !== 'pendiente' 
            ? `${batch.status === 'aprobado' ? 'Aprobado' : 'Rechazado'} por: ${batch.approver || 'N/A'} el ${batch.approvedAt || 'N/A'}`
            : null;
        const generatorName = currentUser?.name || 'Usuario Desconocido';
        const generationDate = new Date().toLocaleString('es-GT', { timeZone: 'America/Guatemala' });
        const generatorLine = `Generado por: ${generatorName} el ${generationDate}`;
        let tableStartY = 62;
        doc.text(requesterLine, 15, 56);
        doc.setFont('helvetica', 'bold');
        doc.text(`Estado: ${statusText}`, 140, 56);
        doc.setFont('helvetica', 'normal');
        if (approverLine) {
            doc.text(approverLine, 15, 62);
            doc.text(generatorLine, 15, 68);
            tableStartY = 76;
        } else {
            doc.text(generatorLine, 15, 62);
            tableStartY = 70;
        }
        const tableColumns = ["Producto", "Diseño", "Talla", "Color", "Ubicación", "Razón", "Cant."];
        const tableRows = batch.items.map(item => [
            item.productName || 'N/A', 
            item.designName || 'Liso', 
            item.size || 'N/A', 
            item.color || 'N/A',
            item.locationName || 'N/A',
            item.reason || 'N/A',
            item.quantity || 0
        ]);
        autoTable(doc, { 
            head: [tableColumns], 
            body: tableRows, 
            startY: tableStartY, 
            headStyles: { fillColor: [22, 160, 133] } 
        });
        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Cantidad Total de Productos en el Lote: ${batch.totalQuantity}`, 15, finalY + 10);
        doc.save(`salida-lote-${batch.batchId}.pdf`);
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'aprobado': return 'bg-green-100 text-green-700';
            case 'pendiente': return 'bg-yellow-100 text-yellow-700';
            case 'rechazado': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md space-y-8">
            <img id="company-logo-for-pdf" src="/aralellum.png" style={{ display: 'none' }} alt="Logo"/>
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">Crear Lote de Salida</h3>
                    {permissions?.INVENTORY?.create_exit && (
                        <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} className="btn-primary">+ Añadir Salida al Lote</button>
                    )}
                </div>
                 <p className="text-gray-600 mb-6">Añade una o más salidas de producto a un lote y luego solicítalo para su aprobación.</p>
            </div>

            {batchItems.length > 0 && (
                <div className="p-4 border rounded-lg bg-sky-50">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold">Lote de Salida Pendiente ({batchItems.length} ítems)</h4>
                        <button onClick={handleSaveExitBatch} className="btn-success">Solicitar Lote de Salida</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Producto</th>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Razón</th>
                                    <th className="p-2 text-right text-sm font-semibold text-gray-600">Cantidad</th>
                                    <th className="p-2 text-center text-sm font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batchItems.map(item => (
                                    <tr key={item.tempId} className="border-b">
                                        {/* --- CAMBIO: Se muestra el diseño del producto --- */}
                                        <td className="p-2">
                                            <p>{item.stockItem.productName} ({item.stockItem.size}, {item.stockItem.color})</p>
                                            {item.stockItem.designName !== 'Sin Diseño' && <p className="text-xs text-indigo-500">{item.stockItem.designName}</p>}
                                        </td>
                                        <td className="p-2">{item.reason}</td>
                                        <td className="p-2 text-right font-bold">{item.quantity}</td>
                                        <td className="p-2 text-center">
                                            {/* --- CAMBIO: Se añade el botón de editar --- */}
                                            <div className="flex justify-center items-center space-x-2">
                                                <button onClick={() => handleStartEdit(item)} className="text-sky-600 hover:text-sky-800 p-1" title="Editar salida">
                                                    <EditIcon />
                                                </button>
                                                <button onClick={() => handleRemoveFromBatch(item.tempId)} className="p-1" title="Quitar del lote">
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {/* --- CAMBIO: Se añade el pie de tabla con el total --- */}
                            <tfoot>
                                <tr className="border-t-2 border-gray-300">
                                    <td colSpan="2" className="p-2 text-right font-semibold text-gray-800">Total del Lote:</td>
                                    <td className="p-2 text-right font-bold text-lg text-gray-800">{batchTotalQuantity}</td>
                                    <td className="p-2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Historial de Lotes de Salida</h3>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px]">
                        <thead className="bg-gray-100">
                           <tr>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Fecha Sol.</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">ID Lote</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Solicitante</th>
                                <th className="p-3 text-right text-sm font-semibold text-gray-600">Cant. Total</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Estado</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan="6" className="text-center p-4">Cargando...</td></tr>) : groupedExits.map(batch => (
                                <tr key={batch.batchId} className="border-b">
                                    <td className="p-3 text-sm">{batch.createdAt}</td>
                                    <td className="p-3 font-mono text-xs">{batch.batchId}</td>
                                    <td className="p-3">{batch.requester}</td>
                                    <td className="p-3 text-right font-bold">{batch.totalQuantity}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(batch.status)}`}>{batch.status}</span>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex gap-4 justify-center">
                                            {batch.status === 'pendiente' && (currentUser.role === 'administrador' || currentUser.role === 'supervisor') && (
                                                <>
                                                    <button onClick={() => handleExitAction(batch, 'aprobado')} className="btn-success text-xs">Aprobar</button>
                                                    <button onClick={() => handleExitAction(batch, 'rechazado')} className="btn-secondary text-xs bg-red-200">Rechazar</button>
                                                </>
                                            )}
                                            <button onClick={() => handleViewDetails(batch)} title="Ver Detalles del Lote">
                                                <EyeIcon />
                                            </button>
                                            <button onClick={() => handlePrintBatch(batch)} title="Imprimir Comprobante"><PrintIcon /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {exits.length === 0 && !isLoading && <p className="text-center text-gray-500 py-8">No hay lotes de salida registrados.</p>}
                </div>
            </div>
            
            {/* --- CAMBIO: El modal ahora es dinámico y usa el stock "congelado" --- */}
            <Modal isOpen={isModalOpen} onClose={handleCancelModal} title={editingItem ? "Editar Salida del Lote" : "Añadir Salida al Lote"}>
                <ExitForm 
                    onSave={editingItem ? handleUpdateBatchItem : handleAddToBatch} 
                    onCancel={handleCancelModal} 
                    stock={availableStockForForm}
                    initialData={editingItem}
                    {...{ currentUser, catalogs }}
                />
            </Modal>
            
            <Modal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} title="Detalle de Lote de Salida">
                <ExitBatchDetailView batch={selectedBatchForDetail} />
            </Modal>
        </div>
    );
};